import { useEffect, type ReactNode } from 'react';
import { onIdTokenChanged, signOut as fbSignOut } from 'firebase/auth';
import { getFirebaseAuth } from './firebase';
import { useAuthStore } from './authStore';
import { resetAllData } from '../stores/reset';
import { setSources } from '../stores/sources';
import { emptySources } from '../stores/sources/empty';
import { createRealSources } from '../stores/sources/real';

/* Keeps the auth store in step with Firebase.
 *
 * onIdTokenChanged rather than onAuthStateChanged: a claim granted by
 * `haus user claims set` reaches the browser only on the next ID-token refresh,
 * and this fires on refresh as well as on sign-in. It still is not instant — a
 * freshly granted operator signs out and back in — but a token that refreshes
 * on its own hour boundary lands here without a reload. */
export function AuthProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const auth = getFirebaseAuth();
    if (!auth) {
      useAuthStore.getState().setUnconfigured();
      return;
    }
    // Each notification supersedes the one before it. getIdTokenResult() is a
    // promise, and without this guard a slow claim read for a user who has
    // since signed out lands on its success path and puts them back — with
    // their superAdmin flag — over a signed-out store.
    let current = 0;
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      current += 1;
      const mine = current;
      if (!user) {
        useAuthStore.getState().setSignedOut();
        resetAllData();
        detachSources();
        return;
      }
      user
        .getIdTokenResult()
        .then((res) => {
          if (mine !== current) return;
          const isSuperAdmin = res.claims.superAdmin === true;
          useAuthStore.getState().setSignedIn(user, isSuperAdmin);
          if (!isSuperAdmin) {
            resetAllData();
            detachSources();
            return;
          }
          // The real lanes are installed ONLY for a verified operator. An
          // account without the claim keeps the empty source, so its screens
          // read "not connected" rather than firing requests that will 403.
          attachSources();
        })
        .catch((err: unknown) => {
          if (mine !== current) return;
          // A claim read that fails is not a grant. Fail closed, and say which
          // of the two it was — an outage and a refusal look identical to a
          // signed-in operator otherwise.
          useAuthStore.getState().setSignedIn(user, false);
          detachSources();
          useAuthStore
            .getState()
            .setError(
              `could not read your access claims: ${
                err instanceof Error ? err.message : String(err)
              }`,
            );
          resetAllData();
        });
    });
    return () => {
      // Unmounting invalidates every pending read as well as the listener.
      current += 1;
      unsubscribe();
      detachSources();
    };
  }, []);

  return <>{children}</>;
}

/** attachSources installs the live lanes, once.
 *
 *  Idempotent because onIdTokenChanged fires on every token refresh — roughly
 *  hourly — and re-installing would swap the sources under live watches for no
 *  reason. */
let attached = false;
function attachSources(): void {
  if (attached) return;
  attached = true;
  setSources(createRealSources());
}

/** detachSources returns to the empty source.
 *
 *  Restoring the EMPTY source rather than a captured predecessor: a captured
 *  one can be the /dev fixture pair if an operator navigated there, and
 *  restoring that into an authed session would show sample data as if it were
 *  real. Signed out means not connected, always. */
function detachSources(): void {
  if (!attached) return;
  attached = false;
  setSources(emptySources);
}

/** refreshClaims forces a new ID token and re-reads the claims on it.
 *
 *  This is the RIGHT action for someone staring at "not an operator" seconds
 *  after being granted the claim. Custom claims are baked into the ID token, so
 *  a session holding a token minted before the grant keeps being refused for up
 *  to an hour — and the only advice the screen could otherwise give is "sign
 *  out and back in", which is a worse version of exactly this.
 *
 *  onIdTokenChanged fires on the refresh, so the listener above re-evaluates
 *  and the screen moves on its own. */
export async function refreshClaims(): Promise<void> {
  const user = useAuthStore.getState().user;
  if (!user) return;
  await user.getIdToken(true);
}

export async function signOut(): Promise<void> {
  const auth = getFirebaseAuth();
  if (auth) await fbSignOut(auth);
  resetAllData();
}
